import type { getPostHogSupportCopy } from '@/i18n/posthog-support-copy';
import type { SupportTicket } from '@/lib/posthog-support/types';

export type SupportIntent = 'problem' | 'question' | 'idea';

export type PostHogSupportView =
  | 'choose'
  | 'compose'
  | 'requests'
  | 'ticket'
  | 'sent';

export type SupportStatusCopyKey =
  | 'received'
  | 'withTeam'
  | 'waitingForYou'
  | 'investigating'
  | 'resolved'
  | 'unknownStatus';

export type SupportStatusTone = 'neutral' | 'active' | 'attention' | 'resolved';

export type SupportPageContext = {
  page: string | null;
  workspace: string | null;
};

/** The request type label recorded on a new ticket. */
export function getSupportIntentLabel(
  copy: Pick<SupportCopy, 'reportProblem' | 'askTeam' | 'shareIdea'>,
  intent: SupportIntent
): string {
  if (intent === 'problem') return copy.reportProblem;
  return intent === 'question' ? copy.askTeam : copy.shareIdea;
}

export function getSupportMessageSubject(message: string): string {
  return (
    message
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find(Boolean) ?? ''
  );
}

type SupportCopy = ReturnType<typeof getPostHogSupportCopy>;
type SupportErrorFallback = 'failedToLoad' | 'failedToSend';

export function getSupportErrorMessage(
  copy: SupportCopy,
  error: string | null,
  fallback: SupportErrorFallback
): string | null {
  if (!error) return null;
  if (error === 'attachmentsUnsupported') return copy.attachmentUnsupported;
  if (error === 'identityChanged') return copy.identityChangedError;
  if (error === 'providerUnavailable') return copy.unavailable;
  if (error === 'ticketAddressMismatch') return copy.ticketAddressMismatchError;
  return copy[fallback];
}

export function getInitialPostHogSupportView({
  initialTicketId,
  initialIntent,
}: {
  initialTicketId?: string;
  initialIntent?: SupportIntent;
}): PostHogSupportView {
  if (initialTicketId) return 'ticket';
  if (initialIntent) return 'compose';
  return 'choose';
}

export function getSupportStatusPresentation(status: string): {
  copyKey: SupportStatusCopyKey;
  tone: SupportStatusTone;
} {
  switch (status.trim().toLowerCase()) {
    case 'new':
    case 'received':
      return { copyKey: 'received', tone: 'neutral' };
    case 'open':
    case 'with_team':
      return { copyKey: 'withTeam', tone: 'active' };
    case 'pending':
    case 'waiting_for_customer':
    case 'waiting_for_you':
      return { copyKey: 'waitingForYou', tone: 'attention' };
    case 'on_hold':
    case 'in_progress':
    case 'investigating':
      return { copyKey: 'investigating', tone: 'active' };
    case 'resolved':
    case 'closed':
    case 'done':
      return { copyKey: 'resolved', tone: 'resolved' };
    default:
      return { copyKey: 'unknownStatus', tone: 'neutral' };
  }
}

export function getSupportPageContext({
  pathname,
  orgSlug,
}: {
  pathname: string | null | undefined;
  orgSlug: string | string[] | null | undefined;
}): SupportPageContext {
  const page = pathname?.trim() || null;
  const workspaceValue = Array.isArray(orgSlug) ? orgSlug[0] : orgSlug;
  const workspace = workspaceValue?.trim() || null;

  return { page, workspace };
}

export function buildContextualSupportMessage({
  message,
  context,
  intentLabel,
  blockingWorkLabel,
  includedDetailsLabel,
  pageLabel,
  workspaceLabel,
}: {
  message: string;
  context: SupportPageContext;
  intentLabel: string;
  blockingWorkLabel?: string;
  includedDetailsLabel: string;
  pageLabel: string;
  workspaceLabel: string;
}): string {
  const trimmedMessage = message.trim();
  const messageLines = [trimmedMessage];
  const detailLines = [
    intentLabel,
    blockingWorkLabel ?? null,
    context.page ? `${pageLabel}: ${context.page}` : null,
    context.workspace ? `${workspaceLabel}: ${context.workspace}` : null,
  ].filter((value): value is string => value !== null);

  messageLines.push('', includedDetailsLabel, ...detailLines);
  return messageLines.join('\n');
}

export function canSendSupportMessage({
  message,
  isSending,
}: {
  message: string;
  isSending: boolean;
}): boolean {
  return message.trim().length > 0 && !isSending;
}

export function getSupportReplyTextareaHeight(scrollHeight: number): number {
  if (!Number.isFinite(scrollHeight)) return 44;
  return Math.min(Math.max(scrollHeight, 44), 128);
}

export function resolveDraftAfterSend<T extends SupportTicket | null>({
  draft,
  result,
}: {
  draft: string;
  result: T;
}): string {
  return result === null ? draft : '';
}

export function getTicketDisplayTitle(ticket: SupportTicket): string {
  const title = ticket.title.trim();
  if (title) return title;
  return `#${ticket.id.slice(0, 8)}`;
}
